import React, { useEffect, useState } from 'react';
import Buddies from '../../atoms/Buddies';
import { TIP_ROUTINES, type BuddyBehaviour } from '../../atoms/Buddies/buddyBehaviours';
import type { BuddiesAct } from '../../atoms/Buddies/buddyScene';
import { BUDDIES_BUSY, BUDDIES_FIT_ORB, BUDDIES_PAIR, type BuddiesWho } from '../../atoms/Buddies/Buddies.constants';
import NetworkOrb from '../../atoms/NetworkOrb';
import SpeechBubble from '../../atoms/SpeechBubble';
import {
  SPEECH_BUBBLE_SIZE_SMALL,
  SPEECH_BUBBLE_TAIL_BOTTOM_START,
  SPEECH_BUBBLE_TAIL_TOP,
} from '../../atoms/SpeechBubble/SpeechBubble.constants';
import { usePrefersReducedMotion } from '../../_internal/usePrefersReducedMotion';
import { ORB_DESIGN_SIZE, ORB_TIPS_BELOW, ORB_TIPS_SIDE, type OrbTipPlacement } from './OrbBuddies.constants';
import { Layer, Root, Tip } from './OrbBuddies.styled';
import { useTipCycle } from './useTipCycle';

export interface OrbBuddiesProps {
  size?: number;
  who?: BuddiesWho;
  /** Working: the buddies run their long busy cycle instead of idling. */
  busy?: boolean;
  /** Shown one after another in a speech bubble; none hides the bubble. */
  tips?: readonly string[];
  tipPlacement?: OrbTipPlacement;
  typingLabel?: string;
  network?: boolean;
  interactive?: boolean;
  className?: string;
  dataTestId?: string;
}

const NO_TIPS: readonly string[] = [];

const OrbBuddies: React.FC<OrbBuddiesProps> = ({
  size = ORB_DESIGN_SIZE,
  who = BUDDIES_PAIR,
  busy = false,
  tips = NO_TIPS,
  tipPlacement = ORB_TIPS_SIDE,
  typingLabel,
  network = true,
  interactive = false,
  className,
  dataTestId = 'orb-buddies',
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const tip = useTipCycle(tips, reducedMotion);
  const [routinesDone, setRoutinesDone] = useState(0);
  const [behaviour, seconds] = TIP_ROUTINES[tip.shownCount % TIP_ROUTINES.length];
  const routine: BuddyBehaviour | null = !reducedMotion && tip.shownCount > routinesDone ? behaviour : null;

  useEffect(() => {
    if (tip.shownCount === 0 || reducedMotion) return undefined;
    const shown = tip.shownCount;
    const id = window.setTimeout(() => setRoutinesDone(shown), seconds * 1000);
    return () => window.clearTimeout(id);
  }, [tip.shownCount, reducedMotion, seconds]);

  const base: BuddiesAct = busy ? BUDDIES_BUSY : 'idle';
  const act: BuddiesAct = tip.phase === 'typing' ? 'listen' : (routine ?? base);
  const tail = tipPlacement === ORB_TIPS_BELOW ? SPEECH_BUBBLE_TAIL_TOP : SPEECH_BUBBLE_TAIL_BOTTOM_START;

  return (
    <Root $size={size} className={className} data-testid={dataTestId}>
      {network && <NetworkOrb size={size} interactive={interactive} dataTestId={`${dataTestId}__network`} />}
      <Layer>
        <Buddies who={who} act={act} size={size} fit={BUDDIES_FIT_ORB} dataTestId={`${dataTestId}__buddies`} />
      </Layer>
      {tip.text !== null && (
        <Tip
          $placement={tipPlacement}
          $size={size}
          $visible={tip.phase !== 'hidden'}
          data-testid={`${dataTestId}__tip`}
          data-phase={tip.phase}
        >
          <SpeechBubble
            tail={tail}
            size={SPEECH_BUBBLE_SIZE_SMALL}
            typing={tip.phase === 'typing'}
            typingLabel={typingLabel}
          >
            {tip.text}
          </SpeechBubble>
        </Tip>
      )}
    </Root>
  );
};

export default OrbBuddies;
